import { getCustomisationValue, updateCustomisationValue } from "../EditablePreview";
import AdvancedTimer from "./advanced-timer/AdvancedTimer";
import AnimatedOverlay from "./animated-overlay/AnimatedOverlay";
import F2uReward from "./f2u/F2uReward";
import GenericProgressBar from "./generic-progress-bar/GenericProgressBar";
import PositionableProgressBar from "./generic-progress-bar/PositionableProgressBar";
import ImageOverlayScaledImage from "./image-overlay-scaled-image/image-overlay-scaled-image";
import LeaderboardList from "./leaderboard-list/LeaderboardList";
import LivePoll from "./live-poll/LivePoll";
import LiveText from "./live-text/LiveText";
import LiveTrivia from "./live-trivia/LiveTrivia";
import NaturalSizeImageBG from "./natural-size-image-bg/natural-size-image-bg";
import FallingObjects from "./object-zapper/FallingObjects";
import ParticleEffects from "./particle-effects/ParticleEffects";
import { convertCustomisationDataToBarSettings } from "./progress-bar/inline-progress-bar";
import RadialProgress from "./radial-progress/radial-progress";
import SlidePuzzle from "./slide-puzzle/SlidePuzzle";
import SlotsRandomiser from "./slots-randomiser/SlotsRandomiser";
import SubbombBg from "./subbomb/subbomb-bg";
import SubbombMainView from "./subbomb/subbomb-main-view";
import WaveHands from "./wave/wave-hands/WaveHands";
import WaveProgressBar from "./wave/wave-progress-bar/WaveProgressBar";
import WaveWin from "./wave/wave-win/WaveWin";

export const CUSTOM_COMPONENTS_LIST = {
    // Live trivia is a top level renderer, the key here matches the id of the feature in the customisation structure
    liveTrivia: {
        render: (props) => <LiveTrivia {...props} />,
        convertStoredValueToVisualisedValue: (field, value, path, node) => {
            // console.log("convertStoredValueToVisualisedValue", field, value, path, node);
            if (path.endsWith('panelWidth')) {
                // Live Poll panel width is a special case, it is a stored as a percentage where 100% is 720 pixels wide,
                // we need to convert this to a pixel value for the visualised node
                const panelWidth = (720 * (value / 100)) + 60;
                // console.log("Converted panelWidth to pixels:", panelWidth);
                return panelWidth;
            }
            if (path.endsWith('hashtagAreaWidth')) {
                // console.log("Converted hashtagAreaWidth to pixels:", value + 50);
                return value + 50;
            }
            if (path.endsWith('imageWidth')) {
                const panelWidth = getCustomisationValue(node.customisationVars, 'hud.trivia.shapeSettings.panelWidth');
                const borderWidth = getCustomisationValue(node.customisationVars, 'hud.trivia.shapeSettings.borderThickness');
                const imageWidthRatio = value / 100;
                const imageWidth = Math.round((panelWidth - borderWidth * 2) * imageWidthRatio);
                // console.log("Converted imageWidth to pixels:", imageWidth);
                return imageWidth;
            }
            return value;
        },
        convertVisualisedValueToStoredValue: (field, value, path, node) => {
            // console.log("convertVisualisedValueToStoredValue", field, value, path, node);
            if (path.endsWith('panelWidth')) {
                // We need to convert the pixel value back to a percentage for storage
                const panelWidth = (((value - 60) / 720) * 100);
                // console.log("Converted panelWidth to percentage:", panelWidth);
                return panelWidth;
            }
            if (path.endsWith('hashtagAreaWidth')) {
                // console.log("Converted hashtagAreaWidth:", value - 50);
                let newVal = value - 50;
                if (newVal < 0) {
                    newVal = 0; // Ensure it doesn't go negative
                }
                return newVal;
            }
            if (path.endsWith('imageWidth')) {
                const panelWidth = getCustomisationValue(node.customisationVars, 'hud.trivia.shapeSettings.panelWidth');
                const borderWidth = getCustomisationValue(node.customisationVars, 'hud.trivia.shapeSettings.borderThickness');
                const innerWidth = panelWidth - (borderWidth * 2);
                if (innerWidth <= 0) {
                    return 2; // Avoid division by zero
                }
                let imageWidthPerc = (value / innerWidth) * 100;
                if (imageWidthPerc < 2) {
                    imageWidthPerc = 2; // Ensure it doesn't go negative
                }
                if (imageWidthPerc > 100) {
                    imageWidthPerc = 100; // Cap at 100%
                }
                // console.log("Converted imageWidth to percentage:", imageWidthPerc);
                return imageWidthPerc;
            }
            return value;
        },
    },
    // Live poll is a top level renderer, the key here matches the id of the feature in the customisation structure
    livePoll: {
        render: (props) => <LivePoll {...props} />,
        convertStoredValueToVisualisedValue: (field, value, path, node) => {
            // console.log("convertStoredValueToVisualisedValue", field, value, path, node);
            if (path.endsWith('panelWidth')) {
                // Live Poll panel width is a special case, it is a stored as a percentage where 100% is 720 pixels wide,
                // we need to convert this to a pixel value for the visualised node
                const panelWidth = (720 * (value / 100)) + 60;
                // console.log("Converted panelWidth to pixels:", panelWidth);
                return panelWidth;
            }
            if (path.endsWith('hashtagAreaWidth')) {
                // console.log("Converted hashtagAreaWidth to pixels:", value + 50);
                return value + 50;
            }
            if (path.endsWith('imageWidth')) {
                const panelWidth = getCustomisationValue(node.customisationVars, 'hud.polls.shapeSettings.panelWidth');
                const borderWidth = getCustomisationValue(node.customisationVars, 'hud.polls.shapeSettings.borderThickness');
                const imageWidthRatio = value / 100;
                const imageWidth = Math.round((panelWidth - borderWidth * 2) * imageWidthRatio);
                // console.log("Converted imageWidth to pixels:", imageWidth);
                return imageWidth;
            }
            return value;
        },
        convertVisualisedValueToStoredValue: (field, value, path, node) => {
            // console.log("convertVisualisedValueToStoredValue", field, value, path, node);
            if (path.endsWith('panelWidth')) {
                // We need to convert the pixel value back to a percentage for storage
                const panelWidth = (((value - 60) / 720) * 100);
                // console.log("Converted panelWidth to percentage:", panelWidth);
                return panelWidth;
            }
            if (path.endsWith('hashtagAreaWidth')) {
                // console.log("Converted hashtagAreaWidth:", value - 50);
                let newVal = value - 50;
                if (newVal < 0) {
                    newVal = 0; // Ensure it doesn't go negative
                }
                return newVal;
            }
            if (path.endsWith('imageWidth')) {
                const panelWidth = getCustomisationValue(node.customisationVars, 'hud.polls.shapeSettings.panelWidth');
                const borderWidth = getCustomisationValue(node.customisationVars, 'hud.polls.shapeSettings.borderThickness');
                const innerWidth = panelWidth - (borderWidth * 2);
                if (innerWidth <= 0) {
                    return 2; // Avoid division by zero
                }
                let imageWidthPerc = (value / innerWidth) * 100;
                if (imageWidthPerc < 2) {
                    imageWidthPerc = 2; // Ensure it doesn't go negative
                }
                if (imageWidthPerc > 100) {
                    imageWidthPerc = 100; // Cap at 100%
                }
                // console.log("Converted imageWidth to percentage:", imageWidthPerc);
                return imageWidthPerc;
            }
            return value;
        },
    },
    // particleEffects is a top level renderer, the key here matches the id of the feature in the customisation structure
    particleEffects: {
        render: (props) => <ParticleEffects {...props} />,
    },
    // Wave Progress Bar is not a top level renderer, it has to be set on a group node to specify how it is rendered
    // Multiple custom renderers can be used for the same feature in this way
    waveProgressBar: {
        render: (props) => <WaveProgressBar {...props} />,
        convertStoredValueToVisualisedValue: (field, value, path, node) => {
            // console.log("convertStoredValueToVisualisedValue", field, value, path, node);
            if (path.endsWith('shape.length')) {
                // Check our orientation, we might need to swap width and height
                const positionSetting = getCustomisationValue(node.customisationVars, 'progressBar.shapeAndPosition.position.position');
                const dir = (positionSetting === 'left' || positionSetting === 'right') ? 'vertical' : 'horizontal';

                // Wave Progress Bar length is stored as a percentage, convert it to pixels
                // For vertical bars, we spit out the thickness value instead
                const length = dir === 'horizontal' ? Math.round(1920 * (value / 100)) : getCustomisationValue(node.customisationVars, 'progressBar.shapeAndPosition.shape.thickness');
                // console.log("Converted shape.length to pixels:", length);
                return length;
            } else 
            if (path.endsWith('shape.thickness')) {
                // Check our orientation, we might need to swap width and height
                const positionSetting = getCustomisationValue(node.customisationVars, 'progressBar.shapeAndPosition.position.position');
                const dir = (positionSetting === 'left' || positionSetting === 'right') ? 'vertical' : 'horizontal';

                const thickness = dir === 'vertical' ? Math.round(1080 * (getCustomisationValue(node.customisationVars, 'progressBar.shapeAndPosition.shape.length') / 100)) : value;
                // console.log("Converted shape.thickness to pixels:", thickness);
                return thickness;
            }
            return value;
        },
        convertVisualisedValueToStoredValue: (field, value, path, node) => {
            // console.log("convertVisualisedValueToStoredValue", field, value, path, node);
            if (path.endsWith('shape.length')) {
                // Check our orientation, we might need to swap width and height
                const positionSetting = getCustomisationValue(node.customisationVars, 'progressBar.shapeAndPosition.position.position');
                const dir = (positionSetting === 'left' || positionSetting === 'right') ? 'vertical' : 'horizontal';

                // Convert the pixel value back to a percentage for storage
                if (dir === 'horizontal') {
                    const length = ((value / 1920) * 100);
                    // console.log("Converted shape.length to percentage:", length);
                    return length;
                } else
                if (dir === 'vertical') {
                    // We have actually been passed in the thickness value, which needs storing in 'progressBar.shapeAndPosition.shape.thickness'
                    updateCustomisationValue(node.customisationVars, 'progressBar.shapeAndPosition.shape.thickness', value);
                    // Return the correct value for this path, which is just pulled out of our data because the next thing the calling function will do is store it.
                    const realLengthValue = getCustomisationValue(node.customisationVars, 'progressBar.shapeAndPosition.shape.length');
                    return realLengthValue;
                }
            } else
            if (path.endsWith('shape.thickness')) {
                // Check our orientation, we might need to swap width and height
                const positionSetting = getCustomisationValue(node.customisationVars, 'progressBar.shapeAndPosition.position.position');
                const dir = (positionSetting === 'left' || positionSetting === 'right') ? 'vertical' : 'horizontal';

                // Convert the pixel value back to a percentage for storage
                if (dir === 'horizontal') {
                    // For horizontal bars we have received a thickness value that just needs returning as-is
                    return value;
                } else
                if (dir === 'vertical') {
                    // For vertical bars we have actually been passed a length value that needs processing and storing in 'progressBar.shapeAndPosition.shape.length'
                    const length = ((value / 1080) * 100);
                    // console.log("Converted shape.length to percentage:", length);
                    updateCustomisationValue(node.customisationVars, 'progressBar.shapeAndPosition.shape.length', length);
                    // We need to retrieve the actual thickness value and return that as-is because the next thing the calling function will do is store it.
                    const realThicknessValue = getCustomisationValue(node.customisationVars, 'progressBar.shapeAndPosition.shape.thickness');
                    return realThicknessValue;
                }
            }

            return value;
        },
    },
    waveHands: {
        render: (props) => <WaveHands {...props} />,
        convertStoredValueToVisualisedValue: (field, value, path, node) => {
            // console.log("convertStoredValueToVisualisedValue", field, value, path, node);
            /* if (path.endsWith('shape.length')) {
                // Wave Hands length is stored as a percentage, convert it to pixels
                const length = Math.round(1920 * (value / 100));
                console.log("Converted shape.length to pixels:", length);
                return length;
            } */
            return value;
        },
        convertVisualisedValueToStoredValue: (field, value, path, node) => {
            // console.log("convertVisualisedValueToStoredValue", field, value, path, node);
            /* if (path.endsWith('shapeAndPosition.shape.length')) {
                // Convert the pixel value back to a percentage for storage
                const length = ((value / 1920) * 100);
                console.log("Converted shape.length to percentage:", length);
                return length;
            } */
            return value;
        },
    },
    waveWin: {
        render: (props) => <WaveWin {...props} />,
    },
    f2uReward: {
        render: (props) => {
            // console.log("Render F2uReward props:", props);
            return <F2uReward {...props} />;
        }
    },
    positionableProgressBar: {
        render: (props) => <PositionableProgressBar {...props} />,
    },
    // Generic Progress Bar is not a top level renderer, it has to be set on a group node to specify how it is rendered
    // Multiple custom renderers can be used for the same feature in this way
    genericProgressBar: {
        render: (props) => <GenericProgressBar {...props} />,
        /* convertStoredValueToVisualisedValue: (field, value, path, node) => {
            console.log("convertStoredValueToVisualisedValue", field, value, path, node);
            if (path.endsWith('shape.length')) {
                // Generic Progress Bar length is stored as a percentage, convert it to pixels
                const length = Math.round(1920 * (value / 100));
                console.log("Converted shape.length to pixels:", length);
                return length;
            }
            return value;
        },
        convertVisualisedValueToStoredValue: (field, value, path, node) => {
            console.log("convertVisualisedValueToStoredValue", field, value, path, node);
            if (path.endsWith('shapeAndPosition.shape.length')) {
                // Convert the pixel value back to a percentage for storage
                const length = ((value / 1920) * 100);
                console.log("Converted shape.length to percentage:", length);
                return length;
            }
            return value;
        }, */
        convertStoredValueToVisualisedValue: (field, value, path, node) => {
            // console.log("convertStoredValueToVisualisedValue", field, value, path, node);
            const pathStart = path.split('.')[0] + '.' + path.split('.')[1];
            if (path.endsWith('shape.length')) {
                // Check our orientation, we might need to swap width and height
                const positionSetting = getCustomisationValue(node.customisationVars, pathStart + '.position.position');
                const dir = (positionSetting === 'left' || positionSetting === 'right' || positionSetting === 'top-left' || positionSetting === 'top-right' || positionSetting === 'bottom-left' || positionSetting === 'bottom-right' || positionSetting === 'middle-left' || positionSetting === 'middle-right') ? 'vertical' : 'horizontal';

                // Wave Progress Bar length is stored as a percentage, convert it to pixels
                // For vertical bars, we spit out the thickness value instead
                const length = dir === 'horizontal' ? Math.round(1920 * (value / 100)) : getCustomisationValue(node.customisationVars, pathStart + '.shape.thickness');
                // console.log("Converted shape.length to pixels:", length);
                return length;
            } else 
            if (path.endsWith('shape.thickness')) {
                // Check our orientation, we might need to swap width and height
                const positionSetting = getCustomisationValue(node.customisationVars, pathStart + '.position.position');
                const dir = (positionSetting === 'left' || positionSetting === 'right' || positionSetting === 'top-left' || positionSetting === 'top-right' || positionSetting === 'bottom-left' || positionSetting === 'bottom-right' || positionSetting === 'middle-left' || positionSetting === 'middle-right') ? 'vertical' : 'horizontal';

                const thickness = dir === 'vertical' ? Math.round(1080 * (getCustomisationValue(node.customisationVars, pathStart + '.shape.length') / 100)) : value;
                // console.log("Converted shape.thickness to pixels:", thickness);
                return thickness;
            }
            return value;
        },
        convertVisualisedValueToStoredValue: (field, value, path, node) => {
            // console.log("convertVisualisedValueToStoredValue", field, value, path, node);
            const pathStart = path.split('.')[0] + '.' + path.split('.')[1];
            if (path.endsWith('shape.length')) {
                // Check our orientation, we might need to swap width and height
                const positionSetting = getCustomisationValue(node.customisationVars, pathStart + '.position.position');
                const dir = (positionSetting === 'left' || positionSetting === 'right' || positionSetting === 'top-left' || positionSetting === 'top-right' || positionSetting === 'bottom-left' || positionSetting === 'bottom-right' || positionSetting === 'middle-left' || positionSetting === 'middle-right') ? 'vertical' : 'horizontal';

                // Convert the pixel value back to a percentage for storage
                if (dir === 'horizontal') {
                    const length = ((value / 1920) * 100);
                    // console.log("Converted shape.length to percentage:", length);
                    return length;
                } else
                if (dir === 'vertical') {
                    // We have actually been passed in the thickness value, which needs storing in 'progressBar.shapeAndPosition.shape.thickness'
                    updateCustomisationValue(node.customisationVars, pathStart + '.shape.thickness', value);
                    // Return the correct value for this path, which is just pulled out of our data because the next thing the calling function will do is store it.
                    const realLengthValue = getCustomisationValue(node.customisationVars, pathStart + '.shape.length');
                    return realLengthValue;
                }
            } else
            if (path.endsWith('shape.thickness')) {
                // Check our orientation, we might need to swap width and height
                const positionSetting = getCustomisationValue(node.customisationVars, pathStart + '.position.position');
                const dir = (positionSetting === 'left' || positionSetting === 'right' || positionSetting === 'top-left' || positionSetting === 'top-right' || positionSetting === 'bottom-left' || positionSetting === 'bottom-right' || positionSetting === 'middle-left' || positionSetting === 'middle-right') ? 'vertical' : 'horizontal';

                // Convert the pixel value back to a percentage for storage
                if (dir === 'horizontal') {
                    // For horizontal bars we have received a thickness value that just needs returning as-is
                    return value;
                } else
                if (dir === 'vertical') {
                    // For vertical bars we have actually been passed a length value that needs processing and storing in 'progressBar.shapeAndPosition.shape.length'
                    const length = ((value / 1080) * 100);
                    // console.log("Converted shape.length to percentage:", length);
                    updateCustomisationValue(node.customisationVars, pathStart + '.shape.length', length);
                    // We need to retrieve the actual thickness value and return that as-is because the next thing the calling function will do is store it.
                    const realThicknessValue = getCustomisationValue(node.customisationVars, pathStart + '.shape.thickness');
                    return realThicknessValue;
                }
            }

            return value;
        },
    },
    radialProgress: {
        render: (props) => <RadialProgress {...props} />,
        convertVisualisedValueToStoredValue: (field, value, path, node, visualisationExtraData) => {
            // console.log("convertVisualisedValueToStoredValue", field, value, path, node, visualisationExtraData);
            if (path.endsWith('size')) {
                // We have the same dataLink for both width and height, so we need to ensure it only updates once!
                if (field.name === 'W') {
                    // console.log("convertVisualisedValueToStoredValue", field, value, path, node, visualisationExtraData);
                    return Math.round((value - 50) / 2);
                } else {
                    return getCustomisationValue(node.customisationVars, path);
                }
            }
            return value;
        },
        convertStoredValueToVisualisedValue: (field, value, path, node, visualisationExtraData) => {
            // console.log("convertStoredValueToVisualisedValue", field, value, path, node, visualisationExtraData);
            if (path.endsWith('size')) {
                return value * 2 + 50;
            }
            return value;
        }
    },
    fallingObjects: {
        render: (props) => {
            // console.log("Render FallingObjects props:", props);
            return <FallingObjects {...props} />;
        }
    },
    slidePuzzle: {
        render: (props) => <SlidePuzzle {...props} />,
        convertVisualisedValueToStoredValue: (field, value, path, node, visualisationExtraData) => {
            if (path.endsWith('size')) {
                // We have the same dataLink for both width and height, so we need to ensure it only updates once!
                if (field.name === 'W') {
                    // console.log("convertVisualisedValueToStoredValue", field, value, path, node, visualisationExtraData);
                    const relativeResizeScale = value / visualisationExtraData;
                    const oldSize = getCustomisationValue(node.customisationVars, path);
                    let size = Math.round(oldSize * relativeResizeScale);
                    if (size < 30) size = 30;
                    if (size > 100) size = 100;
                    // console.log("Converted size to percentage:", size);
                    return size;
                } else {
                    return getCustomisationValue(node.customisationVars, path);
                }
            } else
            if (path.includes('hashtagEmoteSetup')) {
                // Many values on these can be auto set across the 4 different prompts.
                // console.log("convertVisualisedValueToStoredValue (hashtagEmoteSetup)", field, value, path, node, visualisationExtraData);
                if (path.endsWith('pointerScale')) {
                    updateCustomisationValue(node.customisationVars, 'hud.hashtagEmoteSetup.moveAboveTile.pointerScale', value, 0);
                    updateCustomisationValue(node.customisationVars, 'hud.hashtagEmoteSetup.moveBelowTile.pointerScale', value, 0);
                    updateCustomisationValue(node.customisationVars, 'hud.hashtagEmoteSetup.moveLeftTile.pointerScale', value, 0);
                    updateCustomisationValue(node.customisationVars, 'hud.hashtagEmoteSetup.moveRightTile.pointerScale', value, 0);
                    return value;
                }
                if (path.endsWith('showText')) {
                    updateCustomisationValue(node.customisationVars, 'hud.hashtagEmoteSetup.moveAboveTile.showText', value, 0);
                    updateCustomisationValue(node.customisationVars, 'hud.hashtagEmoteSetup.moveBelowTile.showText', value, 0);
                    updateCustomisationValue(node.customisationVars, 'hud.hashtagEmoteSetup.moveLeftTile.showText', value, 0);
                    updateCustomisationValue(node.customisationVars, 'hud.hashtagEmoteSetup.moveRightTile.showText', value, 0);
                    return value;
                }
                if (path.endsWith('showEmoteImage')) {
                    updateCustomisationValue(node.customisationVars, 'hud.hashtagEmoteSetup.moveAboveTile.showEmoteImage', value, 0);
                    updateCustomisationValue(node.customisationVars, 'hud.hashtagEmoteSetup.moveBelowTile.showEmoteImage', value, 0);
                    updateCustomisationValue(node.customisationVars, 'hud.hashtagEmoteSetup.moveLeftTile.showEmoteImage', value, 0);
                    updateCustomisationValue(node.customisationVars, 'hud.hashtagEmoteSetup.moveRightTile.showEmoteImage', value, 0);
                    return value;
                }
                if (path.endsWith('chatTextEmoteSpacing')) {
                    updateCustomisationValue(node.customisationVars, 'hud.hashtagEmoteSetup.moveAboveTile.chatTextEmoteSpacing', value, 0);
                    updateCustomisationValue(node.customisationVars, 'hud.hashtagEmoteSetup.moveBelowTile.chatTextEmoteSpacing', value, 0);
                    updateCustomisationValue(node.customisationVars, 'hud.hashtagEmoteSetup.moveLeftTile.chatTextEmoteSpacing', value, 0);
                    updateCustomisationValue(node.customisationVars, 'hud.hashtagEmoteSetup.moveRightTile.chatTextEmoteSpacing', value, 0);
                    return value;
                }
                if (path.endsWith('emoteScale')) {
                    updateCustomisationValue(node.customisationVars, 'hud.hashtagEmoteSetup.moveAboveTile.emoteScale', value, 0);
                    updateCustomisationValue(node.customisationVars, 'hud.hashtagEmoteSetup.moveBelowTile.emoteScale', value, 0);
                    updateCustomisationValue(node.customisationVars, 'hud.hashtagEmoteSetup.moveLeftTile.emoteScale', value, 0);
                    updateCustomisationValue(node.customisationVars, 'hud.hashtagEmoteSetup.moveRightTile.emoteScale', value, 0);
                    return value;
                }
                if (path.endsWith('chatStringFont')) {
                    updateCustomisationValue(node.customisationVars, 'hud.hashtagEmoteSetup.moveAboveTile.chatStringFont', value, 0);
                    updateCustomisationValue(node.customisationVars, 'hud.hashtagEmoteSetup.moveBelowTile.chatStringFont', value, 0);
                    updateCustomisationValue(node.customisationVars, 'hud.hashtagEmoteSetup.moveLeftTile.chatStringFont', value, 0);
                    updateCustomisationValue(node.customisationVars, 'hud.hashtagEmoteSetup.moveRightTile.chatStringFont', value, 0);
                    return value;
                }
                if (path.endsWith('chatStringFontSize')) {
                    updateCustomisationValue(node.customisationVars, 'hud.hashtagEmoteSetup.moveAboveTile.chatStringFontSize', value, 0);
                    updateCustomisationValue(node.customisationVars, 'hud.hashtagEmoteSetup.moveBelowTile.chatStringFontSize', value, 0);
                    updateCustomisationValue(node.customisationVars, 'hud.hashtagEmoteSetup.moveLeftTile.chatStringFontSize', value, 0);
                    updateCustomisationValue(node.customisationVars, 'hud.hashtagEmoteSetup.moveRightTile.chatStringFontSize', value, 0);
                    return value;
                }
                if (path.endsWith('chatStringIsBold')) {
                    updateCustomisationValue(node.customisationVars, 'hud.hashtagEmoteSetup.moveAboveTile.chatStringIsBold', value, 0);
                    updateCustomisationValue(node.customisationVars, 'hud.hashtagEmoteSetup.moveBelowTile.chatStringIsBold', value, 0);
                    updateCustomisationValue(node.customisationVars, 'hud.hashtagEmoteSetup.moveLeftTile.chatStringIsBold', value, 0);
                    updateCustomisationValue(node.customisationVars, 'hud.hashtagEmoteSetup.moveRightTile.chatStringIsBold', value, 0);
                    return value;
                }
                if (path.endsWith('chatStringFontColour')) {
                    updateCustomisationValue(node.customisationVars, 'hud.hashtagEmoteSetup.moveAboveTile.chatStringFontColour', value, 0);
                    updateCustomisationValue(node.customisationVars, 'hud.hashtagEmoteSetup.moveBelowTile.chatStringFontColour', value, 0);
                    updateCustomisationValue(node.customisationVars, 'hud.hashtagEmoteSetup.moveLeftTile.chatStringFontColour', value, 0);
                    updateCustomisationValue(node.customisationVars, 'hud.hashtagEmoteSetup.moveRightTile.chatStringFontColour', value, 0);
                    return value;
                }
                if (path.endsWith('chatStringOutlineColour')) {
                    updateCustomisationValue(node.customisationVars, 'hud.hashtagEmoteSetup.moveAboveTile.chatStringOutlineColour', value, 0);
                    updateCustomisationValue(node.customisationVars, 'hud.hashtagEmoteSetup.moveBelowTile.chatStringOutlineColour', value, 0);
                    updateCustomisationValue(node.customisationVars, 'hud.hashtagEmoteSetup.moveLeftTile.chatStringOutlineColour', value, 0);
                    updateCustomisationValue(node.customisationVars, 'hud.hashtagEmoteSetup.moveRightTile.chatStringOutlineColour', value, 0);
                    return value;
                }
                if (path.endsWith('chatStringOutlineThickness')) {
                    if (value > 3) value = 3; // Max thickness is 3
                    updateCustomisationValue(node.customisationVars, 'hud.hashtagEmoteSetup.moveAboveTile.chatStringOutlineThickness', value, 0);
                    updateCustomisationValue(node.customisationVars, 'hud.hashtagEmoteSetup.moveBelowTile.chatStringOutlineThickness', value, 0);
                    updateCustomisationValue(node.customisationVars, 'hud.hashtagEmoteSetup.moveLeftTile.chatStringOutlineThickness', value, 0);
                    updateCustomisationValue(node.customisationVars, 'hud.hashtagEmoteSetup.moveRightTile.chatStringOutlineThickness', value, 0);
                    return value;
                }
            }
            return value;
        }
    },
    advancedTimer: {
        render: (props) => <AdvancedTimer {...props} showSecs={true} />,
        convertVisualisedValueToStoredValue: (field, value, path, node, visualisationExtraData) => {
            // console.log("advancedTimer convertVisualisedValueToStoredValue", field, value, path, node, visualisationExtraData);
            if (path.endsWith('shape.length')) {
                // Check our orientation, we might need to swap width and height
                // const positionSetting = getCustomisationValue(node.customisationVars, 'progressBar.shapeAndPosition.position.position');
                // const dir = (positionSetting === 'left' || positionSetting === 'right') ? 'vertical' : 'horizontal';

                // Wave Progress Bar length is stored as a percentage, convert it to pixels
                // For vertical bars, we spit out the thickness value instead
                const length = ((value / 1920) * 100);
                // console.log("advancedTimer Converted shape.length to percentage:", length);
                return length;
            }
            return value;
        },
        convertStoredValueToVisualisedValue: (field, value, path, node, visualisationExtraData) => {
            // console.log("advancedTimer convertStoredValueToVisualisedValue", field, value, path, node, visualisationExtraData);
            if (path.endsWith('shape.length')) {
                // Check our orientation, we might need to swap width and height
                // const positionSetting = getCustomisationValue(node.customisationVars, 'progressBar.shapeAndPosition.position.position');
                // const dir = (positionSetting === 'left' || positionSetting === 'right') ? 'vertical' : 'horizontal';

                // Wave Progress Bar length is stored as a percentage, convert it to pixels
                // For vertical bars, we spit out the thickness value instead
                const length = Math.round(1920 * (value / 100));
                // console.log("advancedTimer Converted shape.length to pixels:", length);
                return length;
            }
            return value;
        },
    },
    liveText: {
        render: (props) => <LiveText {...props} />,
    },
    leaderboardList: {
        render: (props) => <LeaderboardList {...props} />,
    },
    leaderboardListItem: {
        convertVisualisedValueToStoredValue: (field, value, path, node, visualisationExtraData) => {
            console.log("leaderboardListItem convertVisualisedValueToStoredValue", field, value, path, node, visualisationExtraData);
            if (path.endsWith('settings.scoreBoxWidth') || path.endsWith('settings.scoreBoxHeight')) {
                const innerPadding = getCustomisationValue(node.customisationVars, 'visualHUD.visual.settings.scoreBoxPadding');
                const borderThickness = getCustomisationValue(node.customisationVars, 'visualHUD.visual.scoreBoxSettings.borderThickness');
                console.log("leaderboardListItem returned: ", value - (innerPadding * 2), innerPadding);
                return (value - (innerPadding * 2)) - (borderThickness * 2);
            }
            return value;
        },
        convertStoredValueToVisualisedValue: (field, value, path, node, visualisationExtraData) => {
            console.log("leaderboardListItem convertStoredValueToVisualisedValue", field, value, path, node, visualisationExtraData);
            if (path.endsWith('settings.scoreBoxWidth') || path.endsWith('settings.scoreBoxHeight')) {
                const innerPadding = getCustomisationValue(node.customisationVars, 'visualHUD.visual.settings.scoreBoxPadding');
                const borderThickness = getCustomisationValue(node.customisationVars, 'visualHUD.visual.scoreBoxSettings.borderThickness');
                console.log("leaderboardListItem returned: ", value + (innerPadding * 2), innerPadding);
                return value + (innerPadding * 2) + (borderThickness * 2);
            }
            return value;
        },
    },
    naturalSizeImageBg: {
        render: (props) => <NaturalSizeImageBG {...props} />,
    },
    imageOverlayScaledImage: {
        render: (props) => <ImageOverlayScaledImage {...props} />,
    },
    slotsRandomiser: {
        render: (props) => <SlotsRandomiser {...props} />,
    },
    subbombBg: {
        render: (props) => <SubbombBg {...props} />,
    },
    subbombMain: {
        render: (props) => <SubbombMainView {...props} />,
    },
    animatedOverlay: {
        render: (props) => <AnimatedOverlay {...props} />,
    },
}
